<script lang="ts" et-landing-template>
    import { computed, defineComponent, nextTick, type PropType, ref, useId } from 'vue';

    import { useServiceContainer } from '~/container';

    import { useArticleDecisionTree } from '~/modules/landing/composables/useArticleDecisionTree';

    import EtAction from '~/modules/core/components/EtAction.vue';
    import EtIcon from '~/modules/core/components/EtIcon.vue';

    import type { ArticleDecisionAnswer, ArticleDecisionTree } from '~/modules/landing/interfaces';

    import { readArticleDecisionTreeLabels } from '~/modules/landing/utils/readArticleDecisionTreeLabels';

    const TRANSLATION_PREFIX = 'article_visuals.decision_tree';
    const PROGRESS_TOKEN = `${TRANSLATION_PREFIX}.progress`;
    const EMPTY_TREE: ArticleDecisionTree = { title: '', steps: [], finalOutcome: '' };

    export default defineComponent({
        template: '#landingEtArticleDecisionTree',
        components: { EtAction, EtIcon },
        props: {
            tree: {
                type: Object as PropType<ArticleDecisionTree>,
                required: true,
            },
        },
        setup(props) {
            const { TranslationService } = useServiceContainer();

            const translations = TranslationService.translations;
            const labels = readArticleDecisionTreeLabels(translations, TRANSLATION_PREFIX);
            const tree = labels ? props.tree : EMPTY_TREE;
            const { title } = tree;

            const { steps, currentStepIndex, currentStep, canGoBack, outcome, isStepDone, answer, goBack, restart } =
                useArticleDecisionTree(tree);
            const questionId = useId();
            const progressId = useId();
            const questionElement = ref<HTMLElement | null>(null);
            const outcomeElement = ref<HTMLElement | null>(null);

            const progressLabel = computed(() => {
                if (!translations[PROGRESS_TOKEN]) {
                    return '';
                }

                return TranslationService.translate(PROGRESS_TOKEN, {
                    '%current%': currentStepIndex.value + 1,
                    '%total%': steps.length,
                });
            });

            async function focusCurrentView() {
                await nextTick();

                (outcome.value === null ? questionElement.value : outcomeElement.value)?.focus();
            }

            function answerAndFocus(givenAnswer: ArticleDecisionAnswer) {
                answer(givenAnswer);
                focusCurrentView();
            }

            function goBackAndFocus() {
                goBack();
                focusCurrentView();
            }

            function restartAndFocus() {
                restart();
                focusCurrentView();
            }

            return {
                title,
                labels,
                steps,
                currentStep,
                canGoBack,
                questionId,
                progressId,
                questionElement,
                outcomeElement,
                outcome,
                progressLabel,
                isStepDone,
                answer: answerAndFocus,
                goBack: goBackAndFocus,
                restart: restartAndFocus,
            };
        },
    });
</script>
